import React, {useState} from 'react';
import {Link, useLocation, useNavigate} from 'react-router-dom';
import {motion, AnimatePresence} from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import {useAuth} from '../context/AuthContext';

const {FiTarget, FiBarChart3, FiFileText, FiTrendingUp, FiEdit3, FiMenu, FiX, FiUser, FiLogOut, FiTag, FiChevronDown} = FiIcons;

const displayName = (user) => user?.name || user?.email || user?.whatsapp || 'Account';

// Calculators and Dashboard are intentionally hidden from the menu (still
// reachable directly at /calculators and /dashboard) — request from product.
const navItems = [
  {path: '/details', label: 'Details', icon: FiEdit3},
  {path: '/investments', label: 'Investments', icon: FiTrendingUp},
  {path: '/goals', label: 'Goals', icon: FiTarget},
  {path: '/summary', label: 'Summary', icon: FiFileText}
];

function Navigation() {
  const location = useLocation();
  const navigate = useNavigate();
  const {user, logout} = useAuth();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);

  const handleLogout = () => {
    setIsUserMenuOpen(false);
    setIsMobileMenuOpen(false);
    logout();
    navigate('/login', {replace: true});
  };

  return (
    <nav className="bg-white shadow-sm border-b border-gray-200 sticky top-0 z-50">
      <div className="container mx-auto px-4 max-w-7xl">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to="/details" className="flex items-center space-x-2 flex-shrink-0">
            <img
              src="https://quest-media-storage-bucket.s3.us-east-2.amazonaws.com/1750919065872-investt%20png.png"
              alt="iNVESTT.in"
              className="h-8 md:h-10 w-auto"
              onError={(e) => {
                e.target.style.display = 'none';
                e.target.nextSibling.style.display = 'flex';
              }}
            />
            <div
              className="w-8 h-8 md:w-10 md:h-10 rounded-lg bg-[#19b48d] flex items-center justify-center"
              style={{display: 'none'}}
            >
              <SafeIcon icon={FiBarChart3} className="text-white text-base md:text-lg" />
            </div>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center space-x-1">
            {navItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className="relative px-3 py-2 rounded-lg transition-colors duration-200"
              >
                <div className="flex items-center space-x-2">
                  <SafeIcon
                    icon={item.icon}
                    className={`text-sm ${
                      location.pathname === item.path ? 'text-[#19b48d]' : 'text-gray-600'
                    }`}
                  />
                  <span
                    className={`text-sm font-medium ${
                      location.pathname === item.path ? 'text-[#19b48d]' : 'text-gray-600'
                    }`}
                  >
                    {item.label}
                  </span>
                </div>
                {location.pathname === item.path && (
                  <motion.div
                    layoutId="activeTab"
                    className="absolute inset-0 bg-[#19b48d]/10 rounded-lg -z-10"
                    initial={false}
                    transition={{type: 'spring', stiffness: 500, damping: 30}}
                  />
                )}
              </Link>
            ))}
          </div>

          {/* User Menu (desktop) */}
          <div className="hidden lg:block relative flex-shrink-0">
            <button
              onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
              className="flex items-center space-x-2 pl-2 pr-3 py-1.5 rounded-lg hover:bg-gray-100 transition-colors duration-200"
            >
              <div className="w-8 h-8 rounded-full bg-[#19b48d]/10 flex items-center justify-center">
                <SafeIcon icon={FiUser} className="text-[#19b48d] text-sm" />
              </div>
              <span className="text-sm font-medium text-gray-700 max-w-[140px] truncate">
                {displayName(user)}
              </span>
              <SafeIcon icon={FiChevronDown} className="text-gray-400 text-xs" />
            </button>

            <AnimatePresence>
              {isUserMenuOpen && (
                <>
                  <div className="fixed inset-0 z-10" onClick={() => setIsUserMenuOpen(false)} />
                  <motion.div
                    initial={{opacity: 0, y: -8}}
                    animate={{opacity: 1, y: 0}}
                    exit={{opacity: 0, y: -8}}
                    className="absolute right-0 mt-2 w-56 bg-white rounded-xl shadow-lg border border-gray-100 py-2 z-20"
                  >
                    <div className="px-4 py-2 border-b border-gray-100">
                      <p className="text-xs text-gray-400 uppercase tracking-wide">Signed in as</p>
                      <p className="text-sm font-medium text-gray-900 truncate">{displayName(user)}</p>
                    </div>
                    <Link
                      to="/profile"
                      onClick={() => setIsUserMenuOpen(false)}
                      className="flex items-center space-x-2 px-4 py-2 text-sm text-gray-600 hover:bg-gray-50"
                    >
                      <SafeIcon icon={FiUser} className="text-sm" />
                      <span>Profile</span>
                    </Link>
                    <Link
                      to="/pricing"
                      onClick={() => setIsUserMenuOpen(false)}
                      className="flex items-center space-x-2 px-4 py-2 text-sm text-gray-600 hover:bg-gray-50"
                    >
                      <SafeIcon icon={FiTag} className="text-sm" />
                      <span>Pricing</span>
                    </Link>
                    <button
                      onClick={handleLogout}
                      className="w-full flex items-center space-x-2 px-4 py-2 text-sm text-red-600 hover:bg-red-50"
                    >
                      <SafeIcon icon={FiLogOut} className="text-sm" />
                      <span>Logout</span>
                    </button>
                  </motion.div>
                </>
              )}
            </AnimatePresence>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="lg:hidden p-2 text-gray-600 hover:text-[#19b48d] hover:bg-gray-100 rounded-lg transition-colors duration-200"
          >
            <SafeIcon icon={isMobileMenuOpen ? FiX : FiMenu} className="text-xl" />
          </button>
        </div>

        {/* Mobile Navigation */}
        <AnimatePresence>
          {isMobileMenuOpen && (
            <motion.div
              initial={{opacity: 0, height: 0}}
              animate={{opacity: 1, height: 'auto'}}
              exit={{opacity: 0, height: 0}}
              transition={{duration: 0.2}}
              className="lg:hidden overflow-hidden"
            >
              <div className="py-4 space-y-1">
                {navItems.map((item) => (
                  <Link
                    key={item.path}
                    to={item.path}
                    onClick={() => setIsMobileMenuOpen(false)}
                    className={`flex items-center space-x-3 px-4 py-3 rounded-lg transition-colors duration-200 ${
                      location.pathname === item.path
                        ? 'bg-[#19b48d]/10 text-[#19b48d]'
                        : 'text-gray-600 hover:bg-gray-50'
                    }`}
                  >
                    <SafeIcon
                      icon={item.icon}
                      className={`text-lg ${
                        location.pathname === item.path ? 'text-[#19b48d]' : 'text-gray-600'
                      }`}
                    />
                    <span
                      className={`font-medium ${
                        location.pathname === item.path ? 'text-[#19b48d]' : 'text-gray-600'
                      }`}
                    >
                      {item.label}
                    </span>
                  </Link>
                ))}

                <div className="border-t border-gray-100 mt-2 pt-2">
                  <div className="px-4 py-2">
                    <p className="text-xs text-gray-400 uppercase tracking-wide">Signed in as</p>
                    <p className="text-sm font-medium text-gray-900 truncate">{displayName(user)}</p>
                  </div>
                  <Link
                    to="/profile"
                    onClick={() => setIsMobileMenuOpen(false)}
                    className="flex items-center space-x-3 px-4 py-3 rounded-lg text-gray-600 hover:bg-gray-50 transition-colors duration-200"
                  >
                    <SafeIcon icon={FiUser} className="text-lg" />
                    <span className="font-medium">Profile</span>
                  </Link>
                  <Link
                    to="/pricing"
                    onClick={() => setIsMobileMenuOpen(false)}
                    className="flex items-center space-x-3 px-4 py-3 rounded-lg text-gray-600 hover:bg-gray-50 transition-colors duration-200"
                  >
                    <SafeIcon icon={FiTag} className="text-lg" />
                    <span className="font-medium">Pricing</span>
                  </Link>
                  <button
                    onClick={handleLogout}
                    className="w-full flex items-center space-x-3 px-4 py-3 rounded-lg text-red-600 hover:bg-red-50 transition-colors duration-200"
                  >
                    <SafeIcon icon={FiLogOut} className="text-lg" />
                    <span className="font-medium">Logout</span>
                  </button>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </nav>
  );
}

export default Navigation;